import { env } from '@/lib/env';
import { useAuth } from '@/features/auth/auth-store';

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let refreshing: Promise<void> | null = null;

export async function refreshAccessToken(): Promise<void> {
  refreshing ??= (async () => {
    const r = await fetch(`${env.VITE_API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!r.ok) {
      useAuth.getState().set({ status: 'anon', accessToken: null, user: null });
      throw new ApiError('refresh_failed', 'Session expired', r.status);
    }
    const j = (await r.json()) as { access_token: string };
    useAuth.getState().set({ accessToken: j.access_token });
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

async function parseError(r: Response): Promise<ApiError> {
  try {
    const body = (await r.json()) as { error?: { code?: string; message?: string; details?: unknown } };
    const err = body.error ?? {};
    return new ApiError(err.code ?? 'unknown_error', err.message ?? r.statusText, r.status, err.details);
  } catch {
    return new ApiError('unknown_error', r.statusText, r.status);
  }
}

export async function http<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const { accessToken } = useAuth.getState();

  const headers: Record<string, string> = {
    ...(init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(init.headers as Record<string, string> | undefined),
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const url = path.startsWith('http') ? path : `${env.VITE_API_BASE_URL}${path}`;
  let r = await fetch(url, { ...init, headers, credentials: 'include' });

  if (r.status === 401) {
    try {
      await refreshAccessToken();
    } catch {
      throw await parseError(r);
    }
    const { accessToken: fresh } = useAuth.getState();
    if (fresh) headers['Authorization'] = `Bearer ${fresh}`;
    r = await fetch(url, { ...init, headers, credentials: 'include' });
  }

  if (r.status === 204) return undefined as T;
  if (r.ok) return r.json() as Promise<T>;
  throw await parseError(r);
}
